/**
 * Crop load count → crop estimate, for the live preview while a count is
 * being typed. Mirrors HHNodeServer src/utils/cropLoad.js, which stays
 * authoritative: every saved count shows the server's figures. Both pin the
 * same worked example (cropLoad.test.ts / cropLoad.test.js).
 *
 *   fruit per tree = mean (fruit ÷ cross-section) × mean trunk cross-section
 *   fruit          = fruit per tree × trees × (1 − loss %)
 *   tonnes         = fruit × fruit weight (g) ÷ 1,000,000
 *   bins           = tonnes × 1000 ÷ net kg per bin
 */

import type { CropLoadResult, CropLoadSample, CropLoadStage, TrunkMeasure } from '../types/orchard';

export const MIN_SAMPLES = 5;

export const STAGE_LABEL: Record<CropLoadStage, string> = {
  fruit_set: 'At fruit set',
  after_thinning: 'After thinning',
  pre_harvest: 'Before harvest',
};

/** A figure for display, '—' when there is none. */
export const fmt = (v: number | null | undefined, dp = 0): string =>
  v == null ? '—' : v.toLocaleString('en-AU', { minimumFractionDigits: dp, maximumFractionDigits: dp });

const T95 = [NaN, 12.706, 4.303, 3.182, 2.776, 2.571, 2.447, 2.365, 2.306, 2.262, 2.228,
  2.201, 2.179, 2.16, 2.145, 2.131, 2.12, 2.11, 2.101, 2.093, 2.086,
  2.08, 2.074, 2.069, 2.064, 2.06, 2.056, 2.052, 2.048, 2.045, 2.042];
const t95 = (df: number) => (df >= 1 && df <= 30 ? T95[df] : 1.96);

type Num = number | string | null | undefined;
const pos = (v: Num): number | null =>
  v != null && v !== '' && Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : null;
const round = (v: number | null | undefined, dp = 0): number | null =>
  v == null ? null : Math.round(v * 10 ** dp) / 10 ** dp;

/** Cross-sectional area in cm² from a diameter in mm. */
export function areaFromDiameterMm(mm: number): number {
  const r = mm / 20;
  return Math.PI * r * r;
}

/** Trunk cross-sectional area in cm². */
export function trunkArea(value: Num, measure: TrunkMeasure): number | null {
  const v = pos(value);
  if (v == null) return null;
  return measure === 'diameter_mm' ? areaFromDiameterMm(v) : (v * v) / (4 * Math.PI);
}

/** A sample as typed: every number may still be a string or blank. */
export type SampleInput = {
  [K in keyof CropLoadSample]?: K extends 'counted_on' ? CropLoadSample[K] : Num;
};

export interface CropLoadInput {
  trunk_measure: TrunkMeasure;
  samples: SampleInput[];
  tree_count: Num;
  area_hectares?: Num;
  fruit_weight_g: Num;
  bin_kg: Num;
  loss_pct: Num;
}

export function computeCropLoad(input: CropLoadInput): CropLoadResult {
  const warnings: string[] = [];
  const trunks: number[] = [];
  const densities: number[] = [];
  let limbsWithoutDiameter = 0;
  let fruitCounted = 0;

  for (const s of input.samples) {
    const tcsa = trunkArea(s.trunk_value, input.trunk_measure);
    if (tcsa != null) trunks.push(tcsa);
    if (s.fruit_count == null || s.fruit_count === '') continue;
    const fruit = Number(s.fruit_count);
    if (!Number.isFinite(fruit) || fruit < 0) continue;
    let csa: number | null;
    if (s.counted_on === 'limb') {
      const limb = pos(s.limb_diameter_mm);
      if (limb == null) { limbsWithoutDiameter += 1; continue; }
      csa = areaFromDiameterMm(limb);
    } else {
      csa = tcsa;
    }
    if (csa == null) continue;
    densities.push(fruit / csa);
    fruitCounted += fruit;
  }

  const n = densities.length;
  const meanTcsa = trunks.length ? trunks.reduce((t, v) => t + v, 0) / trunks.length : null;
  const meanDensity = n ? densities.reduce((t, v) => t + v, 0) / n : null;
  let sd: number | null = null;
  let half: number | null = null;
  if (n >= 2 && meanDensity != null) {
    sd = Math.sqrt(densities.reduce((t, v) => t + (v - meanDensity) ** 2, 0) / (n - 1));
    half = t95(n - 1) * (sd / Math.sqrt(n));
  }

  if (!trunks.length) warnings.push('No trunk has been measured, so the count cannot be scaled to a tree.');
  if (!n) warnings.push('No fruit has been counted yet.');
  else if (n < MIN_SAMPLES) warnings.push(`Only ${n} ${n === 1 ? 'tree' : 'trees'} counted. Count at least ${MIN_SAMPLES} spread across the block for a figure worth using.`);
  if (limbsWithoutDiameter) warnings.push(`${limbsWithoutDiameter} limb ${limbsWithoutDiameter === 1 ? 'count has' : 'counts have'} no limb diameter and ${limbsWithoutDiameter === 1 ? 'is' : 'are'} left out.`);
  if (meanDensity && sd != null && sd / meanDensity > 0.5) warnings.push('The trees counted vary a lot. More trees will narrow the range.');

  const trees = pos(input.tree_count);
  const weight = pos(input.fruit_weight_g);
  const kg = pos(input.bin_kg);
  const loss = Math.min(100, Math.max(0, Number(input.loss_pct) || 0));
  if (!trees) warnings.push('The cultivar area has no tree count. Set it on the area before using this estimate.');
  if (!weight) warnings.push('Enter the fruit weight expected at harvest to get tonnes and bins.');
  if (weight && !kg) warnings.push('Set the net kg per bin to get bins.');

  const keep = 1 - loss / 100;
  const scale = (density: number | null) => {
    if (density == null || meanTcsa == null) return null;
    const pt = Math.max(0, density) * meanTcsa;
    const fruit = trees ? pt * trees * keep : null;
    const tonnes = fruit != null && weight ? (fruit * weight) / 1e6 : null;
    const bins = tonnes != null && kg ? (tonnes * 1000) / kg : null;
    return { fruit_per_tree: pt, fruit, tonnes, bins };
  };
  const mid = scale(meanDensity);
  const lo = half != null && meanDensity != null ? scale(meanDensity - half) : null;
  const hi = half != null && meanDensity != null ? scale(meanDensity + half) : null;
  const ha = pos(input.area_hectares);
  const perTree = meanDensity != null && meanTcsa != null ? meanDensity * meanTcsa : null;

  return {
    samples_counted: n,
    trunks_measured: trunks.length,
    fruit_counted: fruitCounted,
    mean_tcsa_cm2: round(meanTcsa, 1),
    fruit_per_cm2: round(meanDensity, 2),
    fruit_per_cm2_sd: round(sd, 2),
    fruit_per_tree: round(perTree, 1),
    tree_count: trees,
    loss_pct: loss,
    fruit_weight_g: weight,
    bin_kg: kg,
    total_fruit: mid?.fruit != null ? Math.round(mid.fruit) : null,
    tonnes: round(mid?.tonnes, 2),
    tonnes_per_ha: mid?.tonnes != null && ha ? round(mid.tonnes / ha, 1) : null,
    bins: round(mid?.bins, 1),
    range: lo && hi ? {
      fruit_per_tree: [round(lo.fruit_per_tree, 1)!, round(hi.fruit_per_tree, 1)!],
      tonnes: lo.tonnes != null && hi.tonnes != null ? [round(lo.tonnes, 2)!, round(hi.tonnes, 2)!] : null,
      bins: lo.bins != null && hi.bins != null ? [round(lo.bins, 1)!, round(hi.bins, 1)!] : null,
    } : null,
    warnings,
  };
}
